import { useState, useEffect, useRef, useCallback } from 'react';
import useMobileFocus from '@ajat/design-system/hooks/useMobileFocus';
import { PRODUCT_FOCUS_REVEAL } from './focusReveal';

export default function useProductPage({ imageCount, activeImage, setActiveImage, brand = 'CLS' }) {
  const [isZoomed, setIsZoomed] = useState(false);

  const galleryRef = useRef(null);
  const touchStartX = useRef(0);
  const scrollYBeforeZoom = useRef(0);

  /* Mobile focus card — shared with non-product pages (src/hooks/useMobileFocus.js).
   * PRODUCT_FOCUS_REVEAL says where a field lands on exit: its `.pp__section`,
   * 20px below the sticky `.pp__gallery`. */
  const {
    isMobile,
    focusField,
    activeFocusContainerRef,
    openFocusMode,
    closeFocusMode,
  } = useMobileFocus({ reveal: PRODUCT_FOCUS_REVEAL });

  /* Brand */
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', brand);
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, [brand]);

  /* Keep the active image index in range. Options can carry different image
   * counts (e.g. a 2-image option vs a 1-image one); switching from image #2 of
   * a 2-image option to a single-image option would otherwise leave the gallery
   * parked on a non-existent slide (blank). Reset to 0 when the current index no
   * longer exists in the new image set. */
  useEffect(() => {
    if (imageCount > 0 && activeImage > imageCount - 1) setActiveImage(0);
  }, [imageCount, activeImage, setActiveImage]);

  /* Swipe */
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e) => {
      const diff = touchStartX.current - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        if (diff > 0 && activeImage < imageCount - 1) setActiveImage((i) => i + 1);
        else if (diff < 0 && activeImage > 0) setActiveImage((i) => i - 1);
      }
    },
    [activeImage, imageCount, setActiveImage],
  );

  return {
    isMobile,
    focusField,
    isZoomed,
    setIsZoomed,
    galleryRef,
    scrollYBeforeZoom,
    activeFocusContainerRef,
    handleTouchStart,
    handleTouchEnd,
    openFocusMode,
    closeFocusMode,
  };
}
